import { Box, Button, FormHelperText, TextField } from "@mui/material";
import { useFormik } from "formik";
import { useRouter } from "next/router";
import type { FC } from "react";
import { useDispatch } from "react-redux";
import { reset } from "src/slices/brief";
import { loginNewPassword } from "src/slices/system";
import * as Yup from "yup";
import { useAuth } from "../../hooks/use-auth";
import { useMounted } from "../../hooks/use-mounted";

export const AmplifyLogin: FC = (props) => {
  const isMounted = useMounted();
  const router = useRouter();
  const { login } = useAuth() as any;
  const dispatch = useDispatch();
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      submit: null,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Must be a valid email")
        .max(255)
        .required("Email is required"),
    }),
    onSubmit: async (values, helpers): Promise<void> => {
      dispatch(reset());
      const email = values.email.toLowerCase();
      try {
        const user = await login(email, values.password);

        if (user?.challengeName === "NEW_PASSWORD_REQUIRED") {
          router.push("/authentication/password-complete");
          dispatch(loginNewPassword(user));
        } else if (isMounted()) {
          const loginReturnUrl = router.query.returnUrl as string;

          if (
            loginReturnUrl &&
            loginReturnUrl !== "null" &&
            loginReturnUrl !== "undefined"
          ) {
            router.push(loginReturnUrl);
          } else {
            router.push("/dashboard");
          }
        }
      } catch (err) {
        console.error(err);

        if (isMounted()) {
          if (err.code === "UserNotConfirmedException") {
            sessionStorage.setItem("username", email);
            router.push("/authentication/verify-code");
            return;
          }

          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err.message });
          helpers.setSubmitting(false);
        }
      }
    },
  });

  return (
    <form noValidate onSubmit={formik.handleSubmit} {...props}>
      <TextField
        autoFocus
        error={Boolean(formik.touched.email && formik.errors.email)}
        fullWidth
        helperText={formik.touched.email && formik.errors.email}
        label="Email Address"
        margin="normal"
        name="email"
        onBlur={formik.handleBlur}
        onChange={formik.handleChange}
        type="email"
        value={formik.values.email}
      />
      <TextField
        error={Boolean(formik.touched.password && formik.errors.password)}
        fullWidth
        helperText={formik.touched.password && formik.errors.password}
        label="Password"
        margin="normal"
        name="password"
        onBlur={formik.handleBlur}
        onChange={formik.handleChange}
        type="password"
        value={formik.values.password}
      />
      {formik.errors.submit && (
        <Box sx={{ mt: 3 }}>
          <FormHelperText error>{formik.errors.submit}</FormHelperText>
        </Box>
      )}
      <Box sx={{ mt: 2 }}>
        <Button
          disabled={formik.isSubmitting}
          fullWidth
          size="large"
          type="submit"
          variant="contained"
        >
          Log In
        </Button>
      </Box>
    </form>
  );
};
